import React, { useState, useCallback, useRef } from 'react';
import { Platform } from 'react-native';
import { useTranslation } from '@/hooks/useTranslation';
import { useModalStore, useAuthStore } from '@/store';
import useRequireAuth from '@/hooks/useRequireAuth';
import { MUSIC_STYLES } from '@/constants/config';
import ExplorePage, { ExploreSection } from '@/components/Pages/ExplorePage';
import MusicStyleCarousel from '@/components/MusicStyleCarousel';
import { CarouselNavButtons } from '@/components/ImageStyleCarousel';
import MusicPromptGrid from '@/components/MusicPromptGrid';
import MusicGallery from '@/components/MusicGallery';
import ToolsByOutputTypeSection from '@/components/Tools/ToolsByOutputTypeSection';
import SectionViewAllLink from '@/components/Explore/SectionViewAllLink';
import AppDashboardFooter from '@/components/AppDashboardFooter';

const MusicPage = React.memo(() => {
  const { t } = useTranslation();
  const { openModal } = useModalStore(state => state.actions);
  const hasUser = useAuthStore(state => Boolean(state.user?.id));
  const requireAuth = useRequireAuth();
  const [lyricsValue, setLyricsValue] = useState('');
  const [carouselScrollState, setCarouselScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false,
    showNavButtons: false,
  });
  const carouselRef = useRef(null);

  // Music generations spend credits and write a track to the user's
  // library, so gate both the style-card flow and the bottom lyrics
  // submit before opening the lyrics modal.
  const handleStyleSelect = useCallback(
    (styleData) => {
      requireAuth(
        () => openModal('MusicLyricsModal', { styleData }),
        {
          source: 'music_style_select',
          title: 'Sign in to make music',
          subtitle: 'Music generations are billed to your account so we can apply your credits and save the result.',
          icon: 'music',
        }
      );
    },
    [openModal, requireAuth]
  );

  const handleLyricsSubmit = useCallback(() => {
    const lyrics = lyricsValue?.trim();
    if (!lyrics) return;
    const ran = requireAuth(() => true, {
      source: 'music_lyrics_submit',
      title: 'Sign in to make music',
      subtitle: 'Music generations are billed to your account so we can apply your credits and save the result.',
      icon: 'music',
    });
    if (ran !== true) return;
    const randomStyle = MUSIC_STYLES[Math.floor(Math.random() * MUSIC_STYLES.length)];
    openModal('MusicLyricsModal', { styleData: { ...randomStyle, name: 'Custom' }, initialLyrics: lyrics });
    setLyricsValue('');
  }, [lyricsValue, openModal, requireAuth]);

  const onRefresh = useCallback(async () => {
    await new Promise((resolve) => setTimeout(resolve, 500));
  }, []);

  return (
    <ExplorePage
      metaTitle="Music"
      metaDescription="Create stunning Super Intelligence-generated music and audio. Explore different styles, try various music generation models, and manage your music library."
      metaCanonical="/music"
      searchPlaceholder={t('music.lyricsPlaceholder')}
      searchIcon="music"
      searchValue={lyricsValue}
      onSearchChange={setLyricsValue}
      onSearchSubmit={handleLyricsSubmit}
      onRefresh={onRefresh}
      footer={<AppDashboardFooter fullViewportWidth />}
    >
      <ExploreSection
        title={t('music.tryAStyle')}
        subtitle={t('music.tryAStyleSubtitle')}
        headerRight={
          carouselScrollState.showNavButtons ? (
            <CarouselNavButtons
              canScrollLeft={carouselScrollState.canScrollLeft}
              canScrollRight={carouselScrollState.canScrollRight}
              onScrollLeft={() => carouselRef.current?.scrollBy(-300)}
              onScrollRight={() => carouselRef.current?.scrollBy(300)}
            />
          ) : null
        }
      >
        <MusicStyleCarousel
          ref={carouselRef}
          onStyleSelect={handleStyleSelect}
          onScrollStateChange={setCarouselScrollState}
        />
      </ExploreSection>

      <ExploreSection
        title={t('music.tryOurTools')}
        headerRight={<SectionViewAllLink href="/tools" label={t('common.viewAllTools')} />}
      >
        <ToolsByOutputTypeSection outputType="music" />
      </ExploreSection>

      {/* "Discover something new" prompt grid is hidden for logged-out visitors. */}
      {hasUser && ['web', 'macos'].includes(Platform.OS) && (
        <ExploreSection title={t('music.discoverNew')}>
          <MusicPromptGrid onPromptSelect={handleStyleSelect} />
        </ExploreSection>
      )}

      {/* Personal library is account-bound — hidden for logged-out visitors. */}
      {hasUser && (
        <ExploreSection title={t('music.myMusic')}>
          <MusicGallery />
        </ExploreSection>
      )}
    </ExplorePage>
  );
});

function MusicRoute() {
  return <MusicPage />;
}

export default React.memo(MusicRoute);
